import React, { useEffect } from "react";
import MainLayout from "layouts/MainLayout";
import { useNavigate } from "react-router";
import clsx from "clsx";
import Alert from "components/Alert";
import { useTranslation } from "react-i18next";
import { useAuth0, User } from "@auth0/auth0-react";
import gettingStartImage from "assets/images/getting_started_tile.svg";
import feedbackImage from "assets/images/feedback-icon.svg";
import requestImage from "assets/images/feature_request_tile.svg";
import { observer } from "mobx-react-lite";
import { buildingStore } from "store/Building/BuildingStore";
import InitialLoading from "./Onboarding/InitialLoading";
import { userStore } from "store/UserStore";
import { ROUTES_ENUM } from "routes/consts";
import OrganisationNoLicense from "features/SidebarMenu/components/OrganisationNoLicense";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { LicenseAppType } from "api-client";
import ReleaseNotes from "features/ReleaseNotes";

const Dashboard = observer(() => {
  const { user } = useAuth0<User>();
  const { onboarded, userProfile, verified } = userStore;
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!onboarded)
      return navigate(`/${ROUTES_ENUM.ONBOARDING}`, { replace: true });
    if (!verified) return navigate(`/${ROUTES_ENUM.VERIFY}`, { replace: true });
  }, [onboarded, verified]);

  useEffect(() => {
    buildingStore.setCurrentBuilding(undefined);
  }, []);

  useEffect(() => {
    if (sidebarMenuStore.getCurrentLicense() === LicenseAppType.Asset) {
      navigate(
        `/${sidebarMenuStore.currentOrganisation?.id}/${ROUTES_ENUM.PORTFOLIO_ASSETS}`
      );
    }
  }, [sidebarMenuStore.currentOrganisation]);

  function featureRequestTile() {
    return (
      <div className="border mb-6 border-gray-300 bg-white rounded-lg overflow-hidden">
        <div className="text-base text-gray-900 font-semibold p-4">
          {t("dashboard.featureRequests")}
        </div>
        <div className="flex p-4">
          <img width={120} src={requestImage} />
          <div className="ml-6 text-sm">
            <div className="font-semibold text-gray-900">
              {t("dashboard.haveAWish")}
            </div>
            <div className="text-gray-700 mb-2">
              {t("dashboard.haveAWishText")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function feedbackTile() {
    return (
      <div className="border mb-6 border-gray-300 bg-white rounded-lg overflow-hidden">
        <div className="text-base text-gray-900 font-semibold p-4">
          {t("dashboard.feedbackSupport")}
        </div>
        <div className="flex p-4">
          <img width={120} src={feedbackImage} />
          <div className="ml-6 text-sm">
            <div className="font-semibold text-gray-900">
              {t("dashboard.feedbackSupportTitle")}
            </div>
            <div className="text-gray-700 mb-2">
              {t("dashboard.feedbackSupportText")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function gettingStatedTile() {
    return (
      <div className="border mb-6 border-gray-300 bg-white rounded-lg overflow-hidden">
        <div className="text-base text-gray-900 font-semibold p-4">
          {t("dashboard.gettingStarted")}
        </div>
        <div className="flex p-4">
          <img width={120} src={gettingStartImage} />
          <div className="ml-6 text-sm">
            <div className="font-semibold text-gray-900">
              {t("dashboard.createABuilding")}
            </div>
            <div className="text-gray-700 mb-2">
              {t("dashboard.createABuildingText")}
            </div>
            <div className="font-semibold text-gray-900">
              {t("dashboard.UploadAndMap")}
            </div>
            <div className="text-gray-700 mb-2">
              {t("dashboard.UploadAndMapText")}
            </div>
            <div className="font-semibold text-gray-900">
              {t("dashboard.getResults")}
            </div>
            <div className="text-gray-700 mb-2">
              {t("dashboard.getResultsText")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function dashboardContent(): React.ReactNode {
    return (
      <div className="flex flex-col items-center flex-grow w-full max-w-screen-2xl">
        <h2
          className={clsx(
            "px-3 lg:mb-3 tracking-wide text-center font-medium text-sm",
            "sm:text-xl lg:text-4xl text-gray-90 mt-8"
          )}
        >
          <div>{`${t("dashboard.hello")} ${
            userProfile ? userProfile.first_name : user?.given_name
          },`}</div>
          <div>{t("dashboard.welcomeBack")}</div>
        </h2>
        <Alert
          className="my-8 hidden"
          type="info"
          title={t("dashboard.alertMessage")}
        />
        <div className="mb-8 mt-6 xl:flex md:flex-row flex-col gap-5 w-full">
          <div className="flex-1">
            {gettingStatedTile()}
            {feedbackTile()}
            {featureRequestTile()}
          </div>
          <ReleaseNotes />
        </div>
      </div>
    );
  }

  if (!userProfile?.first_name) return <InitialLoading />;
  return (
    <MainLayout className="px-6 flex justify-center items-center">
      {sidebarMenuStore.getCurrentLicense() ? (
        dashboardContent()
      ) : (
        <OrganisationNoLicense
          organisationID={sidebarMenuStore.currentOrganisation?.id}
          containerClassName="max-w-[550px] flex flex-grow flex-col items-center justify-center"
        />
      )}
    </MainLayout>
  );
});

export default Dashboard;
